import type { DeviceDto } from '@gc/shared';

/**
 * The per-device Actions menu (wave 3 #5) — WHAT the menu offers, computed
 * apart from HOW it is drawn so the permission matrix is testable in plain
 * node. The menu itself (`ActionsMenu.tsx`) renders exactly this list.
 *
 * Permission keys mirror the API gates one-for-one — a menu item an employee
 * cannot execute is a 403 dressed as a feature:
 *   - edit / delete / pair / serviceState -> `devices.manage`
 *     (DevicesController PATCH/DELETE/pairing-code/service-state)
 *   - endReservation -> `sessions.manage` (sessions stop, bookings cancel)
 *   - holdClock / continueClock -> `sessions.manage` (sessions pause/resume)
 *   - voidSession -> `sessions.void` (sessions void)
 *   - outstandingBill -> `bills.view` (BillsController list/detail)
 *
 * **There is deliberately no Checkout here.** Taking money is the till's job
 * and it happens on the Outstanding screen, where a cashier can find a party by
 * name and settle exactly the part of it that is leaving. This menu frees a
 * MACHINE; that is a different act from collecting for it, and a device tile is
 * the wrong place to decide what a group of twelve owes.
 *
 * **`holdClock` / `continueClock` are the BILLING pause, and they are here
 * rather than on the selection bar for the same reason.** They apply to one
 * sitting — a customer who has gone out to eat and should not be charged for
 * the empty chair — not to a sweep of machines, and the answer to "should this
 * party's clock stop" is different for every tile in a selection.
 *
 * They are deliberately NOT called Pause. On this board `Pause` is already
 * taken: with `Play` it is the pair that swings a PC between free-play and the
 * portal login screen (`bulkActions.ts`), nothing to do with money. Two
 * different meanings behind one word on one screen is how staff stop the wrong
 * clock, so the billing pause is spelled out: "Hold the clock" / "Continue".
 */
export type DeviceMenuItemId =
  | 'edit'
  | 'delete'
  | 'outstandingBill'
  | 'endReservation'
  | 'holdClock'
  | 'continueClock'
  | 'voidSession'
  | 'currentCost'
  | 'pair'
  | 'serviceState';

export interface DeviceMenuItem {
  id: DeviceMenuItemId;
  /** Key under the `deviceBoard.menu` namespace (or an absolute key). */
  labelKey: string;
  /** Rendered in the danger colour and separated from the safe actions. */
  danger?: boolean;
}

export function menuItemsFor(
  device: DeviceDto,
  can: (permission: string) => boolean,
): DeviceMenuItem[] {
  const items: DeviceMenuItem[] = [];
  const session = device.currentSession;

  // Display row first: the running cost is the number staff open this menu for.
  if (session) items.push({ id: 'currentCost', labelKey: 'deviceBoard.menu.currentCost' });

  // Holding and continuing are mutually exclusive and both need a live
  // sitting: the API answers 409 for a pause on an already-paused session, so
  // offering both would put a guaranteed error in the menu. Which one shows is
  // read from `session.paused` — the same flag the tile's badge draws from —
  // so the menu and the tile can never disagree about the state of the clock.
  if (can('sessions.manage') && session) {
    items.push(
      session.paused
        ? { id: 'continueClock', labelKey: 'deviceBoard.menu.continueClock' }
        : { id: 'holdClock', labelKey: 'deviceBoard.menu.holdClock' },
    );
  }

  if (can('sessions.manage') && (session || device.nextBooking)) {
    items.push({
      id: 'endReservation',
      labelKey: session ? 'deviceBoard.menu.endReservation' : 'deviceBoard.menu.cancelBooking',
    });
  }

  // Ending a sitting with NOTHING billed is its own privilege (SPEC §5.3), not
  // part of running the floor: the one way play leaves without money. Beside
  // End, in the danger colour, for a live sitting only — a finished unpaid one
  // is voided from the device's Outstanding list, where it is listed.
  if (can('sessions.void') && session) {
    items.push({ id: 'voidSession', labelKey: 'sessionVoid.menu', danger: true });
  }

  if (can('bills.view')) {
    items.push({ id: 'outstandingBill', labelKey: 'deviceBoard.menu.outstandingBill' });
  }

  if (device.deviceType === 'PC' && can('devices.manage')) {
    // The same two conditions PairDeviceAction rendered under when it lived on
    // the tile face: the portal only runs on PCs, and the mint route is gated.
    items.push({ id: 'pair', labelKey: 'pairing.action' });
  }

  if (can('devices.manage')) {
    items.push({ id: 'edit', labelKey: 'deviceBoard.menu.edit' });
    items.push({
      id: 'serviceState',
      labelKey:
        device.status === 'OUT_OF_SERVICE'
          ? 'deviceBoard.menu.backInService'
          : 'deviceBoard.menu.outOfService',
    });
    items.push({ id: 'delete', labelKey: 'deviceBoard.menu.delete', danger: true });
  }

  return items;
}
